import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import SignaturePad from "@/components/patients/SignaturePad";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PATIENTS } from "@/constants/testIds";

function dataUrlToFile(dataUrl, filename) {
  const [header, base64] = dataUrl.split(",");
  const mime = header.match(/:(.*?);/)[1];
  const bytes = atob(base64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new File([arr], filename, { type: mime });
}

const DEFAULT_CONSENT_TEXT =
  "Declaro que he sido informado/a sobre el tratamiento a realizar, sus beneficios, riesgos y alternativas, " +
  "y doy mi consentimiento libre e informado para su realizacion.";

/** Crear un consentimiento informado: texto (o documento subido) + firma
 * digital dibujada en pantalla por el paciente. */
export default function ConsentModal({ open, onOpenChange, patientId, patientName, onCreated }) {
  const [title, setTitle] = useState("");
  const [consentText, setConsentText] = useState(DEFAULT_CONSENT_TEXT);
  const [signedByName, setSignedByName] = useState(patientName || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const signatureRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTitle("");
      setConsentText(DEFAULT_CONSENT_TEXT);
      setSignedByName(patientName || "");
      setError("");
    }
  }, [open, patientName]);

  const handleSave = async () => {
    if (title.trim().length < 2) {
      setError("Ingresa un titulo para el consentimiento");
      return;
    }
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      setError("La firma digital del paciente es obligatoria");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const file = dataUrlToFile(signatureRef.current.getDataUrl(), `firma-${Date.now()}.png`);
      const formData = new FormData();
      formData.append("file", file);
      const { data: uploadData } = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });

      const { data } = await api.post(`/patients/${patientId}/consents`, {
        title: title.trim(),
        consent_text: consentText.trim() || null,
        signature_url: uploadData.url,
        signed_by_name: signedByName.trim() || patientName,
      });
      onCreated(data);
      onOpenChange(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid={PATIENTS.consentModal}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Nuevo consentimiento informado</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Titulo del consentimiento</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid={PATIENTS.consentModalTitleInput} placeholder="Ej: Consentimiento para Toxina Botulinica" />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Texto del consentimiento</Label>
            <Textarea value={consentText} onChange={(e) => setConsentText(e.target.value)} data-testid={PATIENTS.consentModalTextInput} rows={4} />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Firmado por</Label>
            <Input value={signedByName} onChange={(e) => setSignedByName(e.target.value)} data-testid={PATIENTS.consentModalSignedNameInput} />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Firma digital del paciente</Label>
            <SignaturePad ref={signatureRef} />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid={PATIENTS.consentModalCancelButton}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} data-testid={PATIENTS.consentModalSaveButton}>
            {saving ? "Guardando..." : "Guardar consentimiento"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
